import moment from 'moment';
import { useState } from 'react';
import { useIntl } from 'react-intl';
import { ButtonUI, InputSearch, ModalComponent, TaptapList } from 'sonata-react-components';

import './LQlIndexSelector.scss';

import { useTargetingContext } from '@/contexts/Targeting/TargetingContext';
import { getFormattedCountries } from '@/helpers/country.helper';
import LQIMetricsTooltip from '../LQIMetricsTooltip/LQIMetricsTooltip';

type LQIIndexItem = {
  id: string;
  name: string;
  countries: string[];
  version: string;
  created: string;
  metrics: Record<string, unknown>[] | null;
  actions: unknown[];
};

type Props = {
  indexes: Record<string, unknown>[];
  onClose: () => void;
  onSelect: (index: Record<string, unknown>) => void;
};

export default function LQlIndexSelector({ indexes, onClose, onSelect }: Props) {
  const { formatMessage } = useIntl();
  const {
    targeting: { lqi }
  } = useTargetingContext();

  const initialSelected = indexes.find(({ id }) => id === lqi?.prebid?.index);
  const [selected, setSelected] = useState<Record<string, unknown> | undefined>(initialSelected);
  const [search, setSearch] = useState('');

  const onSubmit = () => {
    onSelect(selected!);
    onClose();
  };

  const handleSelectionChange = (items: LQIIndexItem[]) => {
    const last = items[items.length - 1];
    if (!last) {
      setSelected(undefined);
      return;
    }
    const original = indexes.find(idx => String(idx.id) === last.id);
    setSelected(original);
  };

  const filteredIndexes = indexes.filter(index => {
    if (!search) {
      return true;
    }
    const searchLower = search.toLowerCase();
    const name = String(index.name ?? '').toLowerCase();
    const version = String(index.version ?? '').toLowerCase();
    const created = moment(String(index.created ?? '')).format('DD/MM/YYYY');
    return name.includes(searchLower) || version.includes(searchLower) || created.includes(searchLower);
  });

  const dataSource = (index: Record<string, unknown>) => {
    const ds = index.dataSource as Record<string, unknown> | undefined;
    const metrics = ds?.metrics;
    return Array.isArray(metrics) ? (metrics as Record<string, unknown>[]) : null;
  };

  const compositionData: LQIIndexItem[] = filteredIndexes.map(index => ({
    actions: [],
    countries: (index.countries as string[]) ?? [],
    created: String(index.created ?? ''),
    id: String(index.id ?? ''),
    metrics: dataSource(index),
    name: String(index.name ?? ''),
    version: String(index.version ?? '')
  }));

  return (
    <ModalComponent
      isModalOpened
      onClose={onClose}
      portalId="location_quality_indexes"
      theme="light"
      title={formatMessage({
        defaultMessage: 'Location Quality Indexes',
        id: 'targeting.lqi.title'
      })}
      width="840px"
    >
      <ModalComponent.ModalContent>
        <div className="lqiIndexSelector">
          <InputSearch
            className="lqiIndexSelector__search"
            id="lqiIndexSelector-search"
            lens
            name="lqiIndexSelector"
            onChange={setSearch}
            placeholder={formatMessage({
              defaultMessage: 'Search by name, version, date...',
              id: 'targeting.lqi.indexSelector.searchPlaceholder'
            })}
          />
          <TaptapList
            className="lqiIndexSelector__list"
            data={compositionData}
            defaultSelectedItems={
              initialSelected ? compositionData.filter(i => i.id === String(initialSelected.id)) : []
            }
            id="lqiIndexSelector-list"
            onSelectionChange={handleSelectionChange}
            selectionType="rowClick"
          >
            <TaptapList.ColumnsBar>
              <TaptapList.ColumnsBar.Column
                className="name"
                description={formatMessage({
                  defaultMessage: 'Name',
                  id: 'targeting.lqi.indexSelector.fields.name'
                })}
                id="name"
              />
              <TaptapList.ColumnsBar.Column
                className="countries"
                description={formatMessage({
                  defaultMessage: 'Country',
                  id: 'targeting.lqi.indexSelector.fields.country'
                })}
                id="countries"
              />
              <TaptapList.ColumnsBar.Column
                className="version"
                description={formatMessage({
                  defaultMessage: 'Version',
                  id: 'targeting.lqi.indexSelector.fields.version'
                })}
                id="version"
              />
              <TaptapList.ColumnsBar.Column
                className="created"
                description={formatMessage({
                  defaultMessage: 'Created Date',
                  id: 'targeting.lqi.indexSelector.fields.created'
                })}
                id="created"
              />
              <TaptapList.ColumnsBar.Column
                className="metrics"
                description={formatMessage({
                  defaultMessage: 'Metrics',
                  id: 'targeting.lqi.indexSelector.fields.metrics'
                })}
                id="metrics"
              />
            </TaptapList.ColumnsBar>
            <TaptapList.RowsList<LQIIndexItem>
              emptyStateDescription={formatMessage({
                defaultMessage: 'Please change your search terms and try again',
                id: 'targeting.lqi.indexSelector.searchEmptyState.description'
              })}
              emptyStateIcon="fal fa-bolt"
              emptyStateTitle={
                search
                  ? formatMessage({
                      defaultMessage: 'Your search did not match any results',
                      id: 'targeting.lqi.indexSelector.searchEmptyState.title'
                    })
                  : formatMessage({
                      defaultMessage: 'No available indexes were found',
                      id: 'targeting.lqi.indexSelector.emptyState.title'
                    })
              }
            >
              {itemsList =>
                itemsList.map(item => (
                  <TaptapList.RowsList.Row id={item.id} key={item.id}>
                    <span className="name">{item.name}</span>
                    <span className="countries">{getFormattedCountries(item.countries).join(', ')}</span>
                    <span className="version">{item.version}</span>
                    <span className="created">{moment(item.created).format('DD/MM/YYYY')}</span>
                    <span className="metrics">{item.metrics && <LQIMetricsTooltip metrics={item.metrics} />}</span>
                  </TaptapList.RowsList.Row>
                ))
              }
            </TaptapList.RowsList>
          </TaptapList>
        </div>
      </ModalComponent.ModalContent>
      <ModalComponent.CenterButton>
        <ButtonUI
          disabled={!selected}
          onClick={onSubmit}
          shape="squareRounded"
          text={formatMessage({
            defaultMessage: 'Select index',
            id: 'targeting.lqi.indexSelector.selectButton'
          })}
        />
      </ModalComponent.CenterButton>
    </ModalComponent>
  );
}
